import { Box, Typography, Button } from '@mui/material';
import * as React from 'react';
import { useSwipeable } from 'react-swipeable';

const GRID_SIZE = 20;
const CELL_SIZE = 20;
const INITIAL_SPEED = 150;

type Position = {
	x: number;
	y: number;
};

type Direction = 'UP' | 'DOWN' | 'LEFT' | 'RIGHT';

export function GamePage() {
	const [snake, setSnake] = React.useState<Position[]>([{ x: 10, y: 10 }]);
	const [food, setFood] = React.useState<Position>({ x: 5, y: 5 });
	const [direction, setDirection] = React.useState<Direction>('RIGHT');
	const [gameOver, setGameOver] = React.useState(false);
	const [score, setScore] = React.useState(0);
	const [gameStarted, setGameStarted] = React.useState(false);

	const generateFood = () => {
		const newFood = {
			x: Math.floor(Math.random() * GRID_SIZE),
			y: Math.floor(Math.random() * GRID_SIZE),
		};
		setFood(newFood);
	};

	
    

	const moveSnake = () => {
		if (!gameStarted || gameOver) return;

		const head = { ...snake[0] };

		switch (direction) {
		case 'UP':
			head.y -= 1;
			break;
		case 'DOWN':
			head.y += 1;
			break;
		case 'LEFT':
			head.x -= 1;
			break;
		case 'RIGHT':
			head.x += 1;
			break;
		}

		// Check for collisions
		if (
			head.x < 0 ||
			head.x >= GRID_SIZE ||
			head.y < 0 ||
			head.y >= GRID_SIZE ||
			snake.some(segment => segment.x === head.x && segment.y === head.y)
		) {
			setGameOver(true);
			return;
		}

		const newSnake = [head];
		if (head.x === food.x && head.y === food.y) {
			setScore(prev => prev + 1);
			generateFood();
			newSnake.push(...snake);
		} else {
			newSnake.push(...snake.slice(0, -1));
		}

		setSnake(newSnake);
	};

	// Handle swipe controls
	const handlers = useSwipeable({
		onSwiped: () => {
			if (!gameStarted) {
				setGameStarted(true);
				return;
			}},
		onSwipedLeft: () => { if (direction !== 'RIGHT') setDirection('LEFT');},
		onSwipedRight: () => { if (direction !== 'LEFT') setDirection('RIGHT');},
		onSwipedUp: () => { if (direction !== 'DOWN') setDirection('UP');},
		onSwipedDown: () => { if (direction !== 'UP') setDirection('DOWN');},
		swipeDuration: 500,
		preventScrollOnSwipe: true,
		trackMouse: true
	});


	// Handle keyboard controls
	React.useEffect(() => {
		const handleKeyPress = (e: KeyboardEvent) => {
			if (!gameStarted) {
				setGameStarted(true);
				return;
			}

			switch (e.key) {
			case 'ArrowUp':
				if (direction !== 'DOWN') setDirection('UP');
				break;
			case 'ArrowDown':
				if (direction !== 'UP') setDirection('DOWN');
				break;
			case 'ArrowLeft':
				if (direction !== 'RIGHT') setDirection('LEFT');
				break;
			case 'ArrowRight':
				if (direction !== 'LEFT') setDirection('RIGHT');
				break;
			}
		};

		window.addEventListener('keydown', handleKeyPress);
		return () => window.removeEventListener('keydown', handleKeyPress);
	}, [direction, gameStarted]);

	React.useEffect(() => {
		const gameInterval = setInterval(moveSnake, INITIAL_SPEED);
		return () => clearInterval(gameInterval);
	}, [snake, direction, gameOver, gameStarted]);

	const resetGame = () => {
		setSnake([{ x: 10, y: 10 }]);
		setDirection('RIGHT');
		setGameOver(false);
		setScore(0);
		setGameStarted(false);
		generateFood();
	};

	return (
		<Box
			{...handlers}
			sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				minHeight: '90vh',
				gap: 2,
			}}
		>
			<Typography variant="h4">Snake Game</Typography>
			<Typography variant="h6">Score: {score}</Typography>
			
			{gameOver && (
				<Box sx={{ textAlign: 'center', mb: 2 }}>
					<Typography variant="h5" color="error">Game Over!</Typography>
					<Button variant="contained" onClick={resetGame} sx={{ mt: 1 }}>
						Play Again
					</Button>
				</Box>
			)}

			{!gameStarted && !gameOver && (
				<Typography variant="h6">Press any arrow key to start</Typography>
			)}

			<Box
				sx={{
					width: GRID_SIZE * CELL_SIZE,
					height: GRID_SIZE * CELL_SIZE,
					border: '2px solid black',
					position: 'relative',
				}}
			>
				{snake.map((segment, index) => (
					<Box
						key={index}
						sx={{
							position: 'absolute',
							width: CELL_SIZE,
							height: CELL_SIZE,
							backgroundColor: index === 0 ? 'primary.main' : 'primary.light',
							left: segment.x * CELL_SIZE,
							top: segment.y * CELL_SIZE,
						}}
					/>
				))}
				<Box
					sx={{
						position: 'absolute',
						width: CELL_SIZE,
						height: CELL_SIZE,
						backgroundColor: 'error.main',
						left: food.x * CELL_SIZE,
						top: food.y * CELL_SIZE,
					}}
				/>
			</Box>
		</Box>
	);
}
